import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import Button from "../../../components/Button/Button";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { toast } from "react-toastify";

import {
  getAllPassageCeintureDocuments,
  getPublishedPassageCeintureDocuments,
  deletePassageCeintureDocument,
} from "../../../services/passageCeintureDocumentsServices";
import { getBeltsList } from "../../../services/beltServices";

import PassageCeintureDocumentsToolbar from "./components/PassageCeintureDocumentsToolbar";
import PassageCeintureDocumentCard from "./components/ProgressionDocumentCard";
import PassageCeintureDocumentModal from "./components/ProgressionDocumentModal";
import SkeletonCard from "../TechniquesView/SkeletonCard";
import EmptyState from "../TechniquesView/EmptyState";

const DEFAULT_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  showDrafts: false,
  sort: "beltTitle",
  perPage: 24,
};

const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

const strip = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

function beltRank(ceinture = "") {
  const c = strip(ceinture);

  const m = c.match(/(\d+)\s*(?:er|e)?\s*dan/);
  if (m) {
    const n = parseInt(m[1], 10) || 1;
    return 6 + n;
  }

  const colorsFound = BELT_BASE.map((color, idx) =>
    c.includes(color) ? idx : -1
  ).filter((i) => i >= 0);

  if (colorsFound.length === 1) return colorsFound[0];
  if (colorsFound.length >= 2) {
    const avg = colorsFound.reduce((a, b) => a + b, 0) / colorsFound.length;
    return avg;
  }

  const idx = BELT_BASE.indexOf(c);
  if (idx >= 0) return idx;

  return Number.POSITIVE_INFINITY;
}

export default function PassageCeintureDocumentsPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const canManage = [1, 3].includes(Number(user?.role_id));

  const [allDocuments, setAllDocuments] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("passage_ceinture_documents_ui_state");
    return saved ? { ...DEFAULT_STATE, ...JSON.parse(saved) } : DEFAULT_STATE;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search);

  const [opened, setOpened] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem(
      "passage_ceinture_documents_ui_state",
      JSON.stringify(next)
    );
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => persistUi({ ...ui, search: debouncedSearch }),
      300
    );
    return () => clearTimeout(t);
  }, [debouncedSearch, ui, persistUi]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = canManage
        ? await getAllPassageCeintureDocuments()
        : await getPublishedPassageCeintureDocuments();

      setAllDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des documents");
      setAllDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  const loadBelts = useCallback(async () => {
    try {
      const data = await getBeltsList();
      setBelts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des ceintures :", error);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    loadBelts();
  }, [loadDocuments, loadBelts]);

  const dynamicOptions = useMemo(() => {
    const uniqueGrades = Array.from(
      new Set(allDocuments.map((d) => d.ceinture).filter(Boolean))
    );

    uniqueGrades.sort((a, b) => beltRank(a) - beltRank(b));

    return {
      grades: ["Toutes les ceintures", ...uniqueGrades],
    };
  }, [allDocuments]);

  const filtered = useMemo(() => {
    const term = ui.search.trim().toLowerCase();

    let arr = allDocuments.filter((doc) => {
      const matchSearch =
        !term ||
        doc.titre?.toLowerCase().includes(term) ||
        doc.description?.toLowerCase().includes(term) ||
        doc.ceinture?.toLowerCase().includes(term);

      const matchGrade =
        ui.grade === "Toutes les ceintures" || doc.ceinture === ui.grade;

      const matchDraft = canManage
        ? ui.showDrafts
          ? true
          : !!doc.is_published
        : !!doc.is_published;

      return matchSearch && matchGrade && matchDraft;
    });

    arr.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a.titre || ""), String(b.titre || ""));
      }

      if (ui.sort === "belt") {
        return (
          beltRank(a.ceinture) - beltRank(b.ceinture) ||
          collatorFR.compare(String(a.titre || ""), String(b.titre || ""))
        );
      }

      return (
        beltRank(a.ceinture) - beltRank(b.ceinture) ||
        Number(a.ordre_affichage || 0) - Number(b.ordre_affichage || 0) ||
        collatorFR.compare(String(a.titre || ""), String(b.titre || ""))
      );
    });

    return arr;
  }, [allDocuments, ui, canManage]);

  const visible = useMemo(
    () => filtered.slice(0, ui.perPage),
    [filtered, ui.perPage]
  );

  const resetFilters = () => {
    setDebouncedSearch("");
    persistUi(DEFAULT_STATE);
  };

  const loadMore = () => persistUi({ ...ui, perPage: ui.perPage + 24 });

  const handleCreate = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (doc) => {
    setEditing(doc);
    setShowModal(true);
  };

  const handleDelete = async (doc) => {
    const ok = window.confirm(`Supprimer le document "${doc?.titre || ""}" ?`);
    if (!ok) return;

    try {
      await deletePassageCeintureDocument(doc.id);
      toast.success("Document supprimé avec succès");
      await loadDocuments();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression du document");
    }
  };

  return (
    <section className="w-full">
      <ScrollIndicator />
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Passage de ceinture"
          align="center"
          className="mb-1"
        />

        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
          <div className="mb-6">
            <DashboardNavigation role={userRole} />
          </div>

          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
            <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold">
                    Bibliothèque des documents de progression française
                  </h2>
                  <p className="text-white/90 text-sm">
                    Retrouvez les supports de préparation au passage de
                    ceinture : fiches, visuels et documents PDF classés par
                    ceinture.
                    <br />
                    Les enseignants peuvent créer, modifier et supprimer les
                    contenus.
                  </p>
                </div>

                <div className="text-sm">
                  <span className="font-semibold">{filtered.length}</span>{" "}
                  document(s)
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
              <PassageCeintureDocumentsToolbar
                dynamicOptions={dynamicOptions}
                ui={ui}
                canManage={canManage}
                onChange={(patch) => persistUi({ ...ui, ...patch })}
                debouncedSearch={debouncedSearch}
                setDebouncedSearch={setDebouncedSearch}
                onReset={resetFilters}
                onCreate={handleCreate}
              />
            </div>

            <div className="p-4 sm:p-6">
              {loading && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              )}

              {!loading && filtered.length === 0 && (
                <EmptyState
                  title="Aucun document trouvé"
                  description="Aucun document ne correspond aux filtres sélectionnés."
                />
              )}

              {!loading && filtered.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {visible.map((doc) => (
                      <PassageCeintureDocumentCard
                        key={doc.id}
                        document={doc}
                        canManage={canManage}
                        onOpen={() => setOpened(doc)}
                        onEdit={() => handleEdit(doc)}
                        onDelete={() => handleDelete(doc)}
                      />
                    ))}
                  </div>

                  {visible.length < filtered.length && (
                    <div className="text-center mt-6">
                      <Button
                        text="Charger plus"
                        onClick={loadMore}
                        className="rounded-xl"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {opened && (
        <PassageCeintureDocumentModal
          isOpen={!!opened}
          onClose={() => setOpened(null)}
          document={opened}
          belts={belts}
          readOnly
        />
      )}

      {showModal && (
        <PassageCeintureDocumentModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          document={editing}
          belts={belts}
          readOnly={false}
          onSaved={async () => {
            await loadDocuments();
            setShowModal(false);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}